import PropTypes from "prop-types";
import React, { useState } from "react";

// Ordered checklist. `create` is always done (the event exists) and has no button.
// `action` is "editDetails" (Edit Details modal), "addArtist" (inline artist
// picker), "ticketing" (link to the ticketing page), or a tab key (switches the
// event page tab).
const STEPS = [
  { key: "create", title: "Create the event", description: "Name your event and save it to your calendar.", action: null, button: null },
  { key: "dateVenue", title: "Set the date and venue", description: "Choose when and where the event will take place.", action: "editDetails", button: "Set Date & Venue" },
  { key: "lineup", title: "Add your lineup", description: "Add the headliner, support acts, and set times.", action: "addArtist", button: "Add Artists" },
  { key: "tickets", title: "Create ticket types", description: "Set up GA, VIP, pre-sale, or any custom ticket tiers.", action: "ticketing", button: "Create Tickets" },
  { key: "assets", title: "Upload event assets", description: "Add the event poster, flyer, or promotional images.", action: "assets", button: "Upload Assets" },
  { key: "onSale", title: "Set up your on-sale schedule", description: "Configure announce date, pre-sale windows, and public on-sale.", action: "show_details", button: "Schedule Sales" },
  { key: "contacts", title: "Add contacts", description: "Agent, tour manager, venue contact, and other key people.", action: "contacts", button: "Add Contacts" },
];

const VERTICAL_OVERRIDES = {
  performing_arts_center: {
    lineup: { title: "Add your performers", description: "Add the performers, companies, or ensembles in this production.", button: "Add Performers" },
    tickets: { description: "Set up price levels and subscriber, student, or other custom ticket tiers." },
    assets: { description: "Add the production artwork, program, or promotional images." },
    onSale: { description: "Configure announce date, member and subscriber pre-sales, and public on-sale." },
    contacts: { description: "Company manager, production contact, venue contact, and other key people." },
  },
  attractions: {
    lineup: null,
    tickets: { description: "Set up general admission, timed entry, child, member, or other custom ticket tiers." },
    assets: { description: "Add the event's main image, flyer, or promotional images." },
    onSale: { description: "Configure announce date, member early access, and public on-sale." },
    contacts: { description: "Vendors, staff leads, venue contact, and other key people." },
  },
};

const stepsFor = (vertical) => {
  const overrides = VERTICAL_OVERRIDES[vertical] || {};
  return STEPS
    .filter((step) => overrides[step.key] !== null)
    .map((step) => ({ ...step, ...overrides[step.key] }));
};

const isDone = (step, steps) => step.key === "create" || Boolean(steps[step.key]);

const GetEventReadyCard = ({ readiness, vertical, ticketingUrl, onSelectTab, onOpenEditDetails, onAddArtist, isRental, onToggleRental }) => {
  const [collapsed, setCollapsed] = useState(false);
  const steps = readiness.steps || {};
  const visibleSteps = stepsFor(vertical);

  const doneCount = visibleSteps.filter((s) => isDone(s, steps)).length;
  const pct = Math.round((doneCount / visibleSteps.length) * 100);
  const firstIncomplete = visibleSteps.find((s) => !isDone(s, steps));

  const runAction = (step) => {
    if (step.action === "editDetails") onOpenEditDetails();
    else if (step.action === "addArtist") onAddArtist();
    else if (step.action) onSelectTab(step.action);
  };

  return (
    <div className="d2-card ready-card">
      <div className="ready-head">
        <h2 className="ready-title">Get this event ready</h2>
        <span className="ready-count">{doneCount} of {visibleSteps.length}</span>
      </div>
      <p className="ready-sub">Complete these steps to set up your event. You can do them in any order.</p>

      <div className="ready-progress"><div className="ready-progress-fill" style={{ width: `${pct}%` }} /></div>
      <div className="ready-pct">{pct}% complete</div>

      <button type="button" className="ready-toggle" onClick={() => setCollapsed((c) => !c)}>
        {collapsed ? "Show steps" : "Hide steps"} <i className={`fas fa-chevron-${collapsed ? "right" : "down"}`} />
      </button>

      {!collapsed && (
        <ul className="ready-steps">
          {visibleSteps.map((step, i) => {
            const done = isDone(step, steps);
            const primary = firstIncomplete && step.key === firstIncomplete.key;
            const href = step.action === "ticketing" ? ticketingUrl : null;
            const btnClass = `ready-btn${primary ? " is-primary" : ""}`;
            return (
              <li key={step.key} className={`ready-step${done ? " is-done" : ""}`}>
                <span className={`ready-badge${done ? " is-done" : ""}`}>
                  {done ? <i className="fas fa-check" /> : i + 1}
                </span>
                <div className="ready-step-body">
                  <div className="ready-step-title">{step.title}</div>
                  <div className="ready-step-desc">{step.description}</div>
                </div>
                {done ? (
                  <span className="ready-done-badge"><i className="fas fa-check" /> Done</span>
                ) : !step.button ? null : href ? (
                  <a className={btnClass} href={href}>{step.button}</a>
                ) : (
                  <button type="button" className={btnClass} onClick={() => runAction(step)}>
                    {step.button}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {onToggleRental && (
        <div className="ready-rental">
          <div className="ready-step-body">
            <div className="ready-step-title">This is a room rental?</div>
            <div className="ready-step-desc">Turn on if you're renting the room to another organizer.</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isRental}
            aria-label="This is a room rental"
            className={`d2-switch${isRental ? " is-on" : ""}`}
            onClick={() => onToggleRental(!isRental)}
          >
            <span className="d2-switch-knob" />
          </button>
        </div>
      )}
    </div>
  );
};

GetEventReadyCard.propTypes = {
  readiness: PropTypes.shape({ steps: PropTypes.object }).isRequired,
  vertical: PropTypes.string,
  ticketingUrl: PropTypes.string.isRequired,
  onSelectTab: PropTypes.func.isRequired,
  onOpenEditDetails: PropTypes.func.isRequired,
  onAddArtist: PropTypes.func.isRequired,
  isRental: PropTypes.bool,
  onToggleRental: PropTypes.func,
};

export default GetEventReadyCard;
